import { Sparkles, Target, Eye, MessageCircle, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { ParentPriority } from "@/lib/parent/currentPriority";
import type { ParentAnalyticsResult } from "@/hooks/useParentAnalytics";
import { ACCENT_CLS, type AccentKey } from "@/components/parent/shared/parentPrimitives";
import { cn } from "@/lib/utils";

interface Props {
  priority: ParentPriority;
  analytics: ParentAnalyticsResult;
  displayName: string;
}

/**
 * Phase 3 — Next Best Parent Move.
 *
 * Single premium card driven by deriveCurrentPriority. Combines the four
 * pieces a parent needs in 10 seconds:
 *   1. Today's best parent move
 *   2. What to watch
 *   3. Conversation starter
 *   4. Recommended next step (lesson/module) with CTA
 *
 * Data-sufficiency aware: low-data parents see an onboarding-style guide
 * instead of a confidence-flavored prescription.
 */
export default function NextBestParentMove({ priority, analytics, displayName }: Props) {
  const navigate = useNavigate();
  const first = displayName.split(" ")[0] || displayName;
  const sufficiency = analytics.dataSufficiency;
  const lowData = sufficiency.level !== "ready";

  const accent: AccentKey = lowData ? "cyan" : priority.tone === "watch" ? "amber" : "emerald";
  const cls = ACCENT_CLS[accent];

  const ctaLabel = lowData ? "Open learning path" : priority.module === "general" ? "Open learning path" : `Continue ${priority.shortLabel}`;
  const handleCta = () => navigate("/dashboard");


  const items = lowData
    ? [
        { icon: Target, eyebrow: "Today's best move", body: `Sit down with ${first} for one 15-minute first lesson together. Starting matters more than picking the perfect topic.`, accent: "cyan" as AccentKey },
        { icon: Eye, eyebrow: "What to watch", body: "Whether the first session ends with curiosity, not pressure. That's the cadence we want to build.", accent: "amber" as AccentKey },
        { icon: MessageCircle, eyebrow: "Conversation starter", body: "Which money decision do you make most often that you'd love a better answer to?", accent: "violet" as AccentKey, italic: true },
      ]
    : [
        { icon: Target, eyebrow: "Today's best move", body: priority.action.title, sub: priority.action.reason, accent: "emerald" as AccentKey },
        { icon: Eye, eyebrow: "What to watch", body: priority.watch, accent: "amber" as AccentKey },
        { icon: MessageCircle, eyebrow: "Conversation starter", body: priority.prompt.text, sub: priority.prompt.why, accent: "violet" as AccentKey, italic: true },
      ];

  return (
    <section className="relative overflow-hidden rounded-[24px] border border-white/[0.07] ring-1 ring-inset ring-white/[0.04] bg-[linear-gradient(180deg,hsl(var(--card)/0.78)_0%,hsl(var(--card)/0.5)_100%)] p-5 backdrop-blur-2xl shadow-[0_30px_72px_-40px_rgba(0,0,0,0.7),inset_0_1px_0_0_hsl(var(--foreground)/0.06)] sm:p-6">
      <div className={cn("pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent to-transparent", cls.hairline)} />
      <div className="relative">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <span className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 shadow-[inset_0_1px_0_0_hsl(var(--foreground)/0.1)]", cls.chip)}>
              <Sparkles className="h-4.5 w-4.5" />
            </span>
            <div className="min-w-0">
              <p className={cn("text-[10.5px] font-medium uppercase tracking-[0.22em]", cls.text)}>Parent action plan</p>
              <h2 className="mt-0.5 font-heading text-[19px] font-semibold tracking-tight text-foreground sm:text-[22px] leading-[1.2]">
                {lowData ? `Help ${first} get started` : `Best move for ${first} this week`}
              </h2>
              <p className="mt-1 text-[12px] text-muted-foreground">
                {lowData ? "The Parent Hub sharpens as activity builds." : `${priority.moduleLabel}${priority.betaIndex ? ` · step ${priority.betaIndex} of 13` : ""}`}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-5 grid gap-2.5 sm:grid-cols-3">
          {items.map((it) => {
            const subCls = ACCENT_CLS[it.accent];
            return (
              <div key={it.eyebrow} className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-background/30 p-3.5 backdrop-blur">
                <div className={cn("pointer-events-none absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent to-transparent", subCls.hairline)} />
                <div className="relative">
                  <div className="flex items-center gap-2">
                    <span className={cn("flex h-6 w-6 items-center justify-center rounded-lg ring-1", subCls.chip)}>
                      <it.icon className="h-3 w-3" />
                    </span>
                    <p className={cn("text-[10px] font-medium uppercase tracking-[0.18em]", subCls.text)}>{it.eyebrow}</p>
                  </div>
                  <p className={cn("mt-2 text-[13px] leading-snug text-foreground/95", it.italic && "italic")}>
                    {it.italic && <span className="mr-0.5 text-muted-foreground/70">"</span>}
                    {it.body}
                    {it.italic && <span className="ml-0.5 text-muted-foreground/70">"</span>}
                  </p>
                  {"sub" in it && it.sub && (
                    <p className="mt-1.5 text-[10.5px] leading-snug text-muted-foreground/80">{it.sub}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={handleCta}
          className={cn(
            "mt-4 group inline-flex w-full items-center justify-center gap-2 rounded-2xl border px-5 py-3 text-[13px] font-semibold tracking-tight transition",
            "border-cyan-400/30 bg-gradient-to-r from-cyan-500/15 via-cyan-500/10 to-cyan-500/5 text-cyan-100",
            "shadow-[0_12px_30px_-18px_rgba(34,211,238,0.45),inset_0_1px_0_0_rgba(255,255,255,0.08)]",
            "hover:border-cyan-300/40 hover:text-white",
          )}
        >
          {ctaLabel}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </button>

        {lowData && (
          <p className="mt-3 text-[11px] text-muted-foreground/85">
            After {first}'s first quiz or module, this card will surface concept-specific moves automatically.
          </p>
        )}
      </div>
    </section>
  );
}